import { useCallback, useRef, useState, type ChangeEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { uploadFileToExistingProductSource } from '@/features/product-catalog/lib/uploadFileToExistingProductSource'
import { isAllowedProductSourceFile } from '@/features/product-catalog/utils/productSourceFileTypes'
import {
  assertUploadFileReadable,
  assertUploadFileSize,
  isInvalidPdfFileError,
  isPasswordProtectedFileError,
  isRightsProtectedFileError,
  isUploadTooLargeError,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '@/shared/lib/uploadGuardrails'
import { toast } from 'sonner'

export type ReuploadProductSourceFileControls = ReturnType<
  typeof useReuploadProductSourceFile
>

export function useReuploadProductSourceFile(productId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('productCatalog')
  const [uploadingSourceIds, setUploadingSourceIds] = useState<Set<string>>(
    () => new Set()
  )
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingSourceIdRef = useRef<string | null>(null)

  const runReupload = useCallback(
    async (sourceId: string, file: File) => {
      if (!isAllowedProductSourceFile(file)) {
        toast.error(t('sources.upload.allowedFileTypes'))
        return
      }
      try {
        assertUploadFileSize(file)
        await assertUploadFileReadable(file)
      } catch (error) {
        if (isUploadTooLargeError(error)) {
          toast.error(
            t('sources.source.fileTooLarge', {
              maxSizeMb: MAX_UPLOAD_FILE_SIZE_MB,
            })
          )
          return
        }
        if (isPasswordProtectedFileError(error)) {
          toast.error(t('sources.source.fileEncrypted'))
          return
        }
        if (isRightsProtectedFileError(error)) {
          toast.error(t('sources.source.fileRightsProtected'))
          return
        }
        if (isInvalidPdfFileError(error)) {
          toast.error(t('sources.source.fileInvalidPdf'))
          return
        }
        toast.error(t('sources.source.addError'))
        return
      }
      setUploadingSourceIds((previous) => new Set(previous).add(sourceId))
      await uploadFileToExistingProductSource({
        productId,
        sourceId,
        file,
        queryClient,
        translate: t,
        resetUploadStatusToPending: true,
        onFinished: () => {
          setUploadingSourceIds((previous) => {
            const next = new Set(previous)
            next.delete(sourceId)
            return next
          })
        },
      })
    },
    [productId, queryClient, t]
  )

  const openFilePickerForSource = useCallback((sourceId: string) => {
    pendingSourceIdRef.current = sourceId
    fileInputRef.current?.click()
  }, [])

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const sourceId = pendingSourceIdRef.current
      const file = event.target.files?.[0]
      event.target.value = ''
      pendingSourceIdRef.current = null
      if (sourceId == null || file == null) {
        return
      }
      void runReupload(sourceId, file)
    },
    [runReupload]
  )

  return {
    fileInputRef,
    handleFileInputChange,
    openFilePickerForSource,
    uploadingSourceIds,
    isReuploading: (sourceId: string) => uploadingSourceIds.has(sourceId),
  }
}
